import { Ban, Copy, Pencil, Printer, Send, CircleX } from "lucide-react";
import { Link } from "@/lib/nav";
import { useRouter } from "@/lib/nav";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { parseWon } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { sendContract, terminateContract, voidContract } from "@/api/actions/contract";
import { canEditDraft, canSend, canTerminate, canVoid } from "@buildpy/core/modules/contract/transitions";
import { ContractStatus } from "@buildpy/core/modules/contract/types";
import SentLinkNote from "@/modules/share/ui/sent-link-note";

interface Props {
  contractId: string;
  status: ContractStatus;
  // 살아 있는 고객 링크(절대 URL). 없으면 복사 버튼을 숨긴다.
  shareUrl: string | null;
  defaultLast4: string;
  defaultExpiresDate: string;
}

// 날짜 입력은 하루의 끝까지 살려 준다 — 만료일 당일 오전에 링크가 죽으면 사고다.
const endOfDayIso = (date: string): string => {
  const parsed = new Date(`${date}T23:59:59`);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

// 계약 상세의 액션 바 — 어떤 버튼이 보이는지는 전이표(transitions)가 정한다.
const ContractActions = ({ contractId, status, shareUrl, defaultLast4, defaultExpiresDate }: Props) => {
  const router = useRouter();
  const [pending, setPending] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [last4, setLast4] = useState(defaultLast4);
  const [expiresDate, setExpiresDate] = useState(defaultExpiresDate);
  const [sentUrl, setSentUrl] = useState<string | null>(null);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  const [terminateOpen, setTerminateOpen] = useState(false);
  const [terminateReason, setTerminateReason] = useState("");
  const [settled, setSettled] = useState("");

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("링크를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다 — 링크를 직접 선택해 복사해 주세요");
    }
  }, []);

  const send = useCallback(async () => {
    setPending(true);
    try {
      const result = await sendContract(contractId, { last4, expires_at: endOfDayIso(expiresDate) });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      setSentUrl(result.share_url);
      toast.success("발송했습니다 — 링크를 고객에게 보내 주세요");
      router.refresh();
    } catch (error) {
      if (settleRedirect(error)) return;
      toast.error("발송하지 못했습니다");
    } finally {
      setPending(false);
    }
  }, [contractId, last4, expiresDate, router]);

  const doVoid = useCallback(async () => {
    setPending(true);
    try {
      const result = await voidContract(contractId, { reason: voidReason });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success("계약을 파기했습니다");
      setVoidOpen(false);
      router.refresh();
    } catch (error) {
      if (settleRedirect(error)) return;
      toast.error("파기하지 못했습니다");
    } finally {
      setPending(false);
    }
  }, [contractId, voidReason, router]);

  const doTerminate = useCallback(async () => {
    setPending(true);
    try {
      const result = await terminateContract(contractId, {
        reason: terminateReason,
        settled_amount: settled.trim() === "" ? null : parseWon(settled),
      });
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success("계약을 해지했습니다");
      setTerminateOpen(false);
      router.refresh();
    } catch (error) {
      if (settleRedirect(error)) return;
      toast.error("해지하지 못했습니다");
    } finally {
      setPending(false);
    }
  }, [contractId, terminateReason, settled, router]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canEditDraft(status) && (
        <Button asChild size="sm" variant="outline">
          <Link href={`/app/contracts/${contractId}/edit`}>
            <Pencil />
            편집
          </Link>
        </Button>
      )}

      {status === "DRAFT" && canSend(status) && (
        <Button
          disabled={pending}
          size="sm"
          variant="primary"
          onClick={() => {
            setSentUrl(null);
            setSendOpen(true);
          }}>
          <Send />
          발송
        </Button>
      )}

      {status === "SENT" && shareUrl && (
        <Button disabled={pending} size="sm" variant="outline" onClick={() => void copy(shareUrl)}>
          <Copy />
          링크 복사
        </Button>
      )}

      {canVoid(status) && (
        <Button disabled={pending} size="sm" variant="destructive" onClick={() => setVoidOpen(true)}>
          <Ban />
          파기
        </Button>
      )}

      {canTerminate(status) && (
        <Button disabled={pending} size="sm" variant="destructive" onClick={() => setTerminateOpen(true)}>
          <CircleX />
          해지
        </Button>
      )}

      <Button asChild size="sm" variant="outline">
        <Link href={`/app/contracts/${contractId}/print`}>
          <Printer />
          인쇄
        </Link>
      </Button>

      {/* 발송 */}
      <Dialog
        open={sendOpen}
        onOpenChange={(open) => {
          if (!open) setSentUrl(null);
          setSendOpen(open);
        }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sentUrl ? "발송했습니다" : "계약서 발송"}</DialogTitle>
            <DialogDescription>
              {sentUrl ? (
                <SentLinkNote last4={last4} docName="계약서" />
              ) : (
                "고객이 열어 볼 계약서 링크를 만듭니다. 보낸 뒤에는 고칠 수 없습니다."
              )}
            </DialogDescription>
          </DialogHeader>

          {sentUrl ? (
            <div className="flex flex-col gap-y-3">
              <div className="flex items-center gap-x-2">
                <Input readOnly aria-label="고객 링크" inputSize="sm" name="sent-url" value={sentUrl} />
                <Button size="sm" variant="outline" onClick={() => void copy(sentUrl)}>
                  <Copy />
                  복사
                </Button>
              </div>
              <DialogFooter>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    setSentUrl(null);
                    setSendOpen(false);
                  }}>
                  닫기
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex flex-col gap-y-3">
              <Field label="고객 휴대폰 뒤 4자리">
                <Input
                  inputMode="numeric"
                  inputSize="sm"
                  maxLength={4}
                  name="last4"
                  value={last4}
                  onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                />
              </Field>
              <Field label="링크 만료일">
                <Input
                  inputSize="sm"
                  name="expires-date"
                  type="date"
                  value={expiresDate}
                  onChange={(e) => setExpiresDate(e.target.value)}
                />
              </Field>
              <DialogFooter>
                <Button size="sm" variant="ghost" onClick={() => setSendOpen(false)}>
                  취소
                </Button>
                <Button
                  disabled={pending || last4.length !== 4 || !expiresDate}
                  size="sm"
                  variant="primary"
                  onClick={() => void send()}>
                  발송
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 파기 */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>계약 파기</DialogTitle>
            <DialogDescription>
              체결 전 계약을 접습니다. 고객 링크는 바로 닫힙니다. 같은 견적으로 새 계약서를 다시 만들 수 있습니다.
            </DialogDescription>
          </DialogHeader>
          <Field label="사유 (선택 · 300자)">
            <Textarea
              className="min-h-20 text-sm"
              maxLength={300}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setVoidOpen(false)}>
              취소
            </Button>
            <Button disabled={pending} size="sm" variant="destructive" onClick={() => void doVoid()}>
              파기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 해지 */}
      <Dialog open={terminateOpen} onOpenChange={setTerminateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>계약 해지</DialogTitle>
            <DialogDescription>체결한 계약을 중도에 끝냅니다. 사유는 꼭 남깁니다.</DialogDescription>
          </DialogHeader>
          <Field label="사유 (필수 · 300자)">
            <Textarea
              className="min-h-20 text-sm"
              maxLength={300}
              value={terminateReason}
              onChange={(e) => setTerminateReason(e.target.value)}
            />
          </Field>
          <Field label="기성고 정산액 (선택 · 원)">
            <Input
              inputMode="numeric"
              inputSize="sm"
              name="settled"
              value={settled}
              onChange={(e) => setSettled(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setTerminateOpen(false)}>
              취소
            </Button>
            <Button
              disabled={pending || terminateReason.trim().length === 0}
              size="sm"
              variant="destructive"
              onClick={() => void doTerminate()}>
              해지
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractActions;
